/**
 * Top-of-dashboard banner that surfaces when the active workspace's
 * plan is `paused` or its trial has ended. Pre-banner the dashboard
 * still rendered all the action buttons; the user clicked Generate /
 * Connect / Create and got a 402 from the backend with no clear
 * recovery path.
 *
 * Now: a persistent (non-dismissable) banner explains the state, and
 * the CTAs cover the two reasonable next steps:
 *   - Upgrade plan (primary)
 *   - Switch to another workspace (secondary; only shown when the
 *     user belongs to more than one workspace, which is the case the
 *     user explicitly flagged — "I'm also part of a paid workspace
 *     Ludicrous Dukes")
 *
 * Server-side gating still does the real work — every mutation 402s
 * regardless of whether this banner shows. The banner is the UX
 * signal; the backend is the enforcement.
 */

import { useTranslation } from 'react-i18next';
import { AlertOctagon } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { billingApi } from '@/lib/api';
import { useWorkspaceContext } from '@/components/WorkspaceProvider';
import { ButtonLink } from '@/components/ui/Button';

export function WorkspacePausedBanner() {
  const { t } = useTranslation();
  const { activeWorkspace, workspaces } = useWorkspaceContext();
  const workspaceId = activeWorkspace?.id;

  const { data: status } = useQuery({
    queryKey: ['billing-status', workspaceId],
    queryFn: () => billingApi.status(workspaceId!),
    enabled: !!workspaceId,
    staleTime: 60_000,
  });

  if (!status) return null;

  // Two trigger conditions — the rest of the page renders normally.
  // 1. plan === 'paused' (trial expired job ran, or admin paused)
  // 2. plan === 'trial' AND trialEndsAt is in the past (job hasn't
  //    run yet today but the trial is functionally over — banner
  //    should surface immediately, not on a 24h delay)
  const isPaused = status.plan === 'paused';
  const isTrialExpired =
    status.plan === 'trial' &&
    status.trialEndsAt != null &&
    new Date(status.trialEndsAt) <= new Date();
  if (!isPaused && !isTrialExpired) return null;

  const isOwner = activeWorkspace?.role === 'OWNER';
  const otherWorkspaces = workspaces.filter((w) => w.id !== workspaceId);

  return (
    <div role="alert" className="border-b border-red-500/30 bg-red-500/8 px-4 py-3 text-sm md:px-8">
      <div className="mx-auto flex max-w-7xl flex-col items-start gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex items-start gap-3">
          <AlertOctagon className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-500" />
          <div>
            <p className="text-text-primary font-semibold">
              {isPaused ? t('workspace.paused.title') : t('workspace.trialExpired.title')}
            </p>
            <p className="text-text-secondary mt-0.5 text-xs">
              {isPaused ? t('workspace.paused.body') : t('workspace.trialExpired.body')}
            </p>
          </div>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          {isOwner && (
            <ButtonLink to="/dashboard/settings/billing" className="text-xs">
              {t('billing.upgradeCta')}
            </ButtonLink>
          )}
          {otherWorkspaces.length > 0 && (
            <span className="text-text-tertiary text-xs">{t('workspace.paused.switchHint')}</span>
          )}
        </div>
      </div>
    </div>
  );
}
